/*
 * Лента разговора 006 — проекция журнала на сервере (contracts/thread-events.md §5, research R12, FR-020).
 * Браузер получает не сырые события, а строки с постоянными ключами и сливает их по ключу.
 *
 * - Пока ход идёт, видны все его строки.
 * - Закрытый ход сворачивается: под строкой `turn` прячутся шаги, промежуточный текст, запросы и «Меняет черновик»;
 *   видны сообщение человека, итоговый ответ, карточка проекта и системные строки. Шаги свёрнутого хода —
 *   `turnSteps()` (адрес `GET …/turns/:runId/steps`).
 * - Подписи шагов и карточки дают `Labels` (словарь интерфейса и подписи инструментов 005): имён инструментов и JSON
 *   на экране нет (FR-032). Служебные шаги (`rules_about`) строк не дают.
 */
import { z } from 'zod';

export const MCP_PREFIX = 'mcp__efes__';

/** Событие журнала для проекции. `event` — уже проверенное событие AG-UI или `efes.*`. */
export interface TimelineEvent {
  readonly seq: number;
  readonly at: string;
  readonly level: 'turn' | 'thread';
  readonly runId: string | null;
  readonly event: { type: string; [k: string]: unknown };
}

export type StepState = 'running' | 'done' | 'error' | 'denied' | 'stopped';

/** Карточка по результату инструмента (FR-033). */
export type CardPayload =
  | {
      kind: 'draft_change';
      items: { rule: string; field: string; before: string; after: string }[];
      validFrom: string | null;
    }
  | {
      kind: 'project';
      planId: number;
      validFrom: string | null;
      changes: string[];
      expiresAt: string | null;
      needs: string[];
      state: 'active' | 'stale' | 'expired' | 'published';
      stateText: string | null;
    }
  | { kind: 'sample'; label: string };

export interface Labels {
  /** Подпись шага; `null` — служебный шаг, строки нет. */
  step(tool: string, args: unknown, state: StepState): string | null;
  /** Карточка по результату; `null` — обычная строка шага. */
  card(tool: string, args: unknown, result: unknown): CardPayload | null;
  /** Текст системных строк. */
  system: {
    stopped: string;
    stoppedText: string;
    error: (code: string, message: string) => { title: string; text: string };
    modelChanged: (from: string, to: string) => string;
    newSession: string;
    offline: (since: string) => string;
    online: string;
    disabled: string;
  };
}

const base = {
  key: z.string(),
  seq: z.number().int(),
  updatedSeq: z.number().int(),
  runId: z.string().nullable(),
};
export const TimelineRow = z.discriminatedUnion('kind', [
  z.strictObject({
    ...base,
    kind: z.literal('user'),
    text: z.string(),
    note: z.enum(['sent_now', 'sent_after_answer', 'from_queue']).nullable(),
  }),
  z.strictObject({
    ...base,
    kind: z.literal('assistant'),
    markdown: z.string(),
    streaming: z.boolean(),
    interim: z.boolean(),
  }),
  z.strictObject({
    ...base,
    kind: z.literal('step'),
    state: z.enum(['running', 'done', 'error', 'denied', 'stopped']),
    label: z.string(),
    detail: z.string().nullable(),
    jobId: z.string().nullable(),
  }),
  z.strictObject({
    ...base,
    kind: z.literal('draft_change'),
    items: z.array(
      z.strictObject({ rule: z.string(), field: z.string(), before: z.string(), after: z.string() }),
    ),
    validFrom: z.string().nullable(),
  }),
  z.strictObject({
    ...base,
    kind: z.literal('project'),
    planId: z.number().int(),
    validFrom: z.string().nullable(),
    changes: z.array(z.string()),
    expiresAt: z.string().nullable(),
    needs: z.array(z.string()),
    state: z.enum(['active', 'stale', 'expired', 'published']),
    stateText: z.string().nullable(),
  }),
  z.strictObject({ ...base, kind: z.literal('sample'), label: z.string() }),
  z.strictObject({
    ...base,
    kind: z.literal('request'),
    interruptId: z.string(),
    reason: z.enum(['approval', 'question']),
    state: z.enum([
      'waiting',
      'allowed_once',
      'allowed_thread',
      'denied',
      'cancelled',
      'expired',
      'answered',
    ]),
    card: z
      .strictObject({
        title: z.string().optional(),
        what: z.string(),
        why: z.string(),
        yes: z.string(),
        no: z.string(),
      })
      .nullable(),
    question: z
      .strictObject({
        text: z.string(),
        options: z.array(z.strictObject({ label: z.string(), description: z.string().optional() })),
        multiSelect: z.boolean().optional(),
      })
      .nullable(),
    decisions: z.array(z.string()),
    expiresAt: z.string().nullable(),
    by: z.string().nullable(),
    /** Одноразовый номер ответа: сервер ставит его владельцу, пока запрос ждёт (FR-027). В журнале его нет. */
    nonce: z.string().nullable(),
  }),
  z.strictObject({
    ...base,
    kind: z.literal('system'),
    tone: z.enum(['info', 'warn', 'bad', 'mute']),
    title: z.string(),
    text: z.string(),
  }),
  z.strictObject({
    ...base,
    kind: z.literal('turn'),
    collapsed: z.boolean(),
    outcome: z.enum(['running', 'waiting', 'success', 'cancelled', 'error']),
    startedAt: z.string(),
    endedAt: z.string().nullable(),
    workedMs: z.number().int().nullable(),
    steps: z.number().int(),
    totalTokens: z.number().int().nullable(),
  }),
]);
export type TimelineRow = z.infer<typeof TimelineRow>;
type Kind = TimelineRow['kind'];

/** Строки, которые прячутся под свёрнутым ходом. */
const INNER: ReadonlySet<Kind> = new Set(['step', 'draft_change', 'request', 'sample']);

interface Built {
  /** Все строки по порядку: [seq, sub]. */
  rows: (TimelineRow & { order: [number, number]; inner: boolean })[];
  /** Закрытые ходы: runId → seq закрытия. */
  closedRuns: Map<string, number>;
}

function parseJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return undefined;
  }
}

function contentText(c: unknown): string {
  if (typeof c === 'string') return c;
  if (Array.isArray(c))
    return c
      .map((p) => (p && typeof p === 'object' && 'text' in p ? String((p as { text: unknown }).text) : ''))
      .join('');
  return '';
}

function build(events: readonly TimelineEvent[], labels: Labels): Built {
  const rows = new Map<string, TimelineRow & { order: [number, number]; inner: boolean }>();
  const closedRuns = new Map<string, number>();
  const toolArgs = new Map<string, string>();
  const toolName = new Map<string, string>();
  const runMessages = new Map<string, string[]>();
  const runUsage = new Map<string, number>();
  const runSteps = new Map<string, number>();
  let lastModel: string | null = null;

  const put = <R extends TimelineRow>(row: R, order: [number, number], inner: boolean) => {
    const prev = rows.get(row.key);
    rows.set(row.key, { ...row, order: prev?.order ?? order, inner, seq: prev?.seq ?? row.seq });
  };
  const upd = (key: string, seq: number, patch: Partial<TimelineRow>) => {
    const prev = rows.get(key);
    if (!prev) return;
    rows.set(key, { ...prev, ...patch, updatedSeq: seq } as typeof prev);
  };
  const turnKey = (runId: string) => `turn:${runId}`;

  for (const e of events) {
    const ev = e.event;
    const runId = e.runId;
    const s = e.seq;
    switch (ev.type) {
      case 'RUN_STARTED': {
        if (!runId) break;
        const input = ev.input as
          | { messages?: { id: string; content: unknown; metadata?: Record<string, unknown> }[] }
          | undefined;
        const msg = input?.messages?.[0];
        if (msg) {
          const note =
            (msg.metadata?.['efes/note'] as 'from_queue' | 'sent_after_answer' | undefined) ?? null;
          put(
            {
              key: `msg:${msg.id}`,
              kind: 'user',
              seq: s,
              updatedSeq: s,
              runId,
              text: contentText(msg.content),
              note,
            },
            [s, 0],
            false,
          );
        }
        put(
          {
            key: turnKey(runId),
            kind: 'turn',
            seq: s,
            updatedSeq: s,
            runId,
            collapsed: false,
            outcome: 'running',
            startedAt: e.at,
            endedAt: null,
            workedMs: null,
            steps: 0,
            totalTokens: null,
          },
          [s, 1],
          false,
        );
        runMessages.set(runId, []);
        break;
      }
      case 'TEXT_MESSAGE_START': {
        const id = String(ev.messageId);
        if (ev.role === 'user') {
          put(
            { key: `msg:${id}`, kind: 'user', seq: s, updatedSeq: s, runId, text: '', note: 'sent_now' },
            [s, 0],
            false,
          );
        } else {
          put(
            {
              key: `msg:${id}`,
              kind: 'assistant',
              seq: s,
              updatedSeq: s,
              runId,
              markdown: '',
              streaming: true,
              interim: false,
            },
            [s, 0],
            false,
          );
          if (runId) runMessages.get(runId)?.push(`msg:${id}`);
        }
        break;
      }
      case 'TEXT_MESSAGE_CONTENT': {
        const key = `msg:${String(ev.messageId)}`;
        const prev = rows.get(key);
        if (prev?.kind === 'assistant') upd(key, s, { markdown: prev.markdown + String(ev.delta) });
        else if (prev?.kind === 'user') upd(key, s, { text: prev.text + String(ev.delta) });
        break;
      }
      case 'TEXT_MESSAGE_END': {
        const key = `msg:${String(ev.messageId)}`;
        if (rows.get(key)?.kind === 'assistant') upd(key, s, { streaming: false });
        break;
      }
      case 'TOOL_CALL_START': {
        const id = String(ev.toolCallId);
        const tool = String(ev.toolCallName).startsWith(MCP_PREFIX)
          ? String(ev.toolCallName).slice(MCP_PREFIX.length)
          : String(ev.toolCallName);
        toolName.set(id, tool);
        toolArgs.set(id, '');
        const label = labels.step(tool, undefined, 'running');
        if (label === null) break;
        put(
          {
            key: `tool:${id}`,
            kind: 'step',
            seq: s,
            updatedSeq: s,
            runId,
            state: 'running',
            label,
            detail: null,
            jobId: null,
          },
          [s, 0],
          true,
        );
        if (runId) runSteps.set(runId, (runSteps.get(runId) ?? 0) + 1);
        break;
      }
      case 'TOOL_CALL_ARGS': {
        const id = String(ev.toolCallId);
        toolArgs.set(id, (toolArgs.get(id) ?? '') + String(ev.delta));
        const tool = toolName.get(id);
        const key = `tool:${id}`;
        if (tool && rows.has(key)) {
          const label = labels.step(tool, parseJson(toolArgs.get(id) ?? ''), 'running');
          if (label) upd(key, s, { label });
        }
        break;
      }
      case 'TOOL_CALL_RESULT': {
        const id = String(ev.toolCallId);
        const tool = toolName.get(id) ?? '';
        const meta = (ev.metadata ?? {}) as Record<string, unknown>;
        const status = (meta['efes/status'] as string | undefined) ?? 'ok';
        const args = parseJson(toolArgs.get(id) ?? '');
        const key = `tool:${id}`;
        const sampleId = meta['efes/sampleId'];
        if (status === 'ok') {
          const result = parseJson(contentText(ev.content)) ?? contentText(ev.content);
          const label = labels.step(tool, args, 'done');
          if (label !== null && rows.has(key)) upd(key, s, { state: 'done', label });
          const card =
            sampleId !== undefined
              ? ({ kind: 'sample', label: contentText(ev.content) } as const)
              : labels.card(tool, args, result);
          if (card?.kind === 'sample') {
            put(
              {
                key: `sample:${id}`,
                kind: 'sample',
                seq: s,
                updatedSeq: s,
                runId,
                label: card.label,
              },
              [s, 1],
              true,
            );
          } else if (card?.kind === 'draft_change') {
            put(
              {
                key: `draft:${id}`,
                kind: 'draft_change',
                seq: s,
                updatedSeq: s,
                runId,
                items: card.items,
                validFrom: card.validFrom,
              },
              [s, 1],
              true,
            );
          } else if (card?.kind === 'project') {
            const { kind: _k, ...p } = card;
            put(
              { key: `project:${card.planId}`, kind: 'project', seq: s, updatedSeq: s, runId, ...p },
              [s, 1],
              false,
            );
          }
        } else if (rows.has(key)) {
          const state = status === 'denied' ? 'denied' : 'error';
          const label = labels.step(tool, args, state);
          upd(key, s, {
            state,
            ...(label === null ? {} : { label }),
            detail: (meta['efes/error'] as string | undefined) ?? null,
          });
        }
        break;
      }
      case 'ACTIVITY_SNAPSHOT': {
        if (ev.activityType !== 'efes.calculation') break;
        const c = ev.content as { toolCallId?: string; jobId?: string };
        if (c.toolCallId && rows.has(`tool:${c.toolCallId}`))
          upd(`tool:${c.toolCallId}`, s, { jobId: c.jobId ?? null });
        break;
      }
      case 'CUSTOM': {
        const name = String(ev.name);
        const value = ev.value as Record<string, unknown>;
        if (name === 'efes.request.opened') {
          const it = value.interrupt as { id: string; reason: 'approval' | 'question'; expiresAt?: string };
          put(
            {
              key: `req:${it.id}`,
              kind: 'request',
              seq: s,
              updatedSeq: s,
              runId,
              interruptId: it.id,
              reason: it.reason,
              state: 'waiting',
              card:
                (value.card as
                  | { title?: string; what: string; why: string; yes: string; no: string }
                  | undefined) ?? null,
              question:
                (value.question as
                  | {
                      text: string;
                      options: { label: string; description?: string }[];
                      multiSelect?: boolean;
                    }
                  | undefined) ?? null,
              decisions: (value.decisions as string[]) ?? [],
              expiresAt: it.expiresAt ?? null,
              by: null,
              nonce: null,
            },
            [s, 0],
            true,
          );
          if (runId) upd(turnKey(runId), s, { outcome: 'waiting' });
        } else if (name === 'efes.request.closed') {
          const r = value.resume as { interruptId: string; status: string; payload?: { decision?: string } };
          const cause = String(value.cause);
          const decision = r.payload?.decision;
          const state =
            cause === 'expired'
              ? 'expired'
              : r.status === 'cancelled'
                ? 'cancelled'
                : decision === 'deny'
                  ? 'denied'
                  : decision === 'allow_thread'
                    ? 'allowed_thread'
                    : decision === 'allow_once'
                      ? 'allowed_once'
                      : 'answered';
          upd(`req:${r.interruptId}`, s, { state, by: String(value.by) });
          if (runId && rows.get(turnKey(runId))) upd(turnKey(runId), s, { outcome: 'running' });
        } else if (name === 'efes.model') {
          const m = String(value.model);
          if (lastModel !== null && lastModel !== m && value.source === 'agent') {
            put(
              {
                key: `sys:${s}`,
                kind: 'system',
                seq: s,
                updatedSeq: s,
                runId,
                tone: 'info',
                title: labels.system.modelChanged(lastModel, m),
                text: '',
              },
              [s, 0],
              false,
            );
          }
          lastModel = m;
        } else if (name === 'efes.session' && value.state === 'new_after_failure') {
          put(
            {
              key: `sys:${s}`,
              kind: 'system',
              seq: s,
              updatedSeq: s,
              runId,
              tone: 'warn',
              title: labels.system.newSession,
              text: '',
            },
            [s, 0],
            false,
          );
        } else if (name === 'efes.computer') {
          const st = String(value.state);
          const title =
            st === 'offline'
              ? labels.system.offline(String(value.since))
              : st === 'disabled'
                ? labels.system.disabled
                : labels.system.online;
          put(
            {
              key: `sys:${s}`,
              kind: 'system',
              seq: s,
              updatedSeq: s,
              runId: null,
              tone: st === 'online' ? 'info' : 'warn',
              title,
              text: '',
            },
            [s, 0],
            false,
          );
        }
        break;
      }
      case 'RUN_FINISHED':
      case 'RUN_ERROR': {
        if (!runId) {
          // Ход не начался: только RUN_ERROR без RUN_STARTED (FR-024).
          const err = labels.system.error(String(ev.code ?? 'agent_failed'), String(ev.message ?? ''));
          put(
            { key: `sys:${s}`, kind: 'system', seq: s, updatedSeq: s, runId: null, tone: 'bad', ...err },
            [s, 0],
            false,
          );
          break;
        }
        closedRuns.set(runId, s);
        const cancelled =
          ev.type === 'RUN_FINISHED' && (ev.outcome as { type?: string } | undefined)?.type === 'cancelled';
        const outcome = ev.type === 'RUN_ERROR' ? 'error' : cancelled ? 'cancelled' : 'success';
        const usage = (ev.usage as { totalTokens?: number }[] | undefined) ?? [];
        const total = usage.reduce((a, u) => a + (u.totalTokens ?? 0), 0);
        runUsage.set(runId, total);
        const tk = turnKey(runId);
        const tr = rows.get(tk);
        if (tr?.kind === 'turn') {
          const workedMs = Math.max(0, Date.parse(e.at) - Date.parse(tr.startedAt));
          upd(tk, s, {
            collapsed: true,
            outcome,
            endedAt: e.at,
            workedMs: Number.isFinite(workedMs) ? workedMs : null,
            steps: runSteps.get(runId) ?? 0,
            totalTokens: usage.length ? total : null,
          });
        }
        // Незакрытые шаги закрытого хода — «остановлено».
        for (const [k, r] of rows) {
          if (r.runId === runId && r.kind === 'step' && r.state === 'running')
            upd(k, s, { state: 'stopped' });
          if (r.runId === runId && r.kind === 'assistant' && r.streaming) upd(k, s, { streaming: false });
        }
        // Промежуточный текст — все ответы хода, кроме последнего.
        const msgs = runMessages.get(runId) ?? [];
        for (const k of msgs.slice(0, -1)) {
          const r = rows.get(k);
          if (r?.kind === 'assistant') rows.set(k, { ...r, interim: true, inner: true });
        }
        if (cancelled) {
          put(
            {
              key: `sys:${s}`,
              kind: 'system',
              seq: s,
              updatedSeq: s,
              runId,
              tone: 'mute',
              title: labels.system.stopped,
              text: labels.system.stoppedText,
            },
            [s, 0],
            false,
          );
        } else if (ev.type === 'RUN_ERROR') {
          const err = labels.system.error(String(ev.code ?? 'agent_failed'), String(ev.message ?? ''));
          put(
            { key: `sys:${s}`, kind: 'system', seq: s, updatedSeq: s, runId, tone: 'bad', ...err },
            [s, 0],
            false,
          );
        }
        break;
      }
    }
  }
  const sorted = [...rows.values()].sort((a, b) => a.order[0] - b.order[0] || a.order[1] - b.order[1]);
  return { rows: sorted, closedRuns };
}

const strip = (r: TimelineRow & { order?: unknown; inner?: unknown }): TimelineRow => {
  const { order: _o, inner: _i, ...rest } = r;
  return rest as TimelineRow;
};

/** Лента: строки для экрана. Шаги закрытых ходов спрятаны под строкой `turn`. */
export function project(
  events: readonly TimelineEvent[],
  labels: Labels,
): { seq: number; rows: TimelineRow[] } {
  const { rows, closedRuns } = build(events, labels);
  const visible = rows.filter((r) => !(r.inner && r.runId && closedRuns.has(r.runId)));
  return { seq: events.at(-1)?.seq ?? 0, rows: visible.map(strip) };
}

/** Шаги свёрнутого хода (`GET …/turns/:runId/steps`). */
export function turnSteps(events: readonly TimelineEvent[], runId: string, labels: Labels): TimelineRow[] {
  const { rows } = build(events, labels);
  return rows.filter((r) => r.runId === runId && r.inner).map(strip);
}

/** Догрузка: строки, изменённые после `afterSeq`, и ключи строк, которые ушли под свёрнутый ход. */
export function diff(
  events: readonly TimelineEvent[],
  labels: Labels,
  afterSeq: number,
): { seq: number; rows: TimelineRow[]; removed: string[] } {
  const { rows, closedRuns } = build(events, labels);
  const removed: string[] = [];
  const changed: TimelineRow[] = [];
  for (const r of rows) {
    const hidden = r.inner && r.runId !== null && closedRuns.has(r.runId);
    if (hidden) {
      if ((closedRuns.get(r.runId as string) ?? 0) > afterSeq && r.seq <= afterSeq) removed.push(r.key);
      continue;
    }
    if (r.updatedSeq > afterSeq) changed.push(strip(r));
  }
  return { seq: events.at(-1)?.seq ?? 0, rows: changed, removed };
}
